/* ================================================= */
/* UNIFIED PASTORAL NARRATIVE LAYER                  */
/* 100% deterministic — no AI, no ML, no LLM calls   */
/*                                                   */
/* Reads all pastoral engine outputs and produces    */
/* ONE dominant theme, ONE primary action, and a     */
/* suppressedSignals set so redundant components     */
/* fall silent.                                      */
/*                                                   */
/* This engine orchestrates — it does not replace.   */
/* The narrative has no visual output of its own;    */
/* it tells the UI what to show and what to hide.    */
/*                                                   */
/* Designed for extension into:                      */
/* - AI assistant context building                   */
/* - Voice pastoral briefings                        */
/* - Weekly digest generation                        */
/* - Pastoral recap mode                             */
/* ================================================= */

import { TREND_TYPES } from "./pastoral-memory";
import { WORKFLOW_STAGES, WORKFLOW_URGENCY, WORKFLOW_TYPES } from "./pastoral-workflows";
import { LOAD_LEVEL, FOCUS_TONE } from "./pastoral-scheduling";

/* ============================= */
/* DOMINANT THEMES               */
/* Full taxonomy of pastoral     */
/* states — every situation maps */
/* to exactly one theme.         */
/* ============================= */

export const NARRATIVE_THEME = {
  // Situation requires action
  CRISIS_CLUSTER:       "CRISIS_CLUSTER",       // multi-dimensional: prayers + absence + spiritual
  CONTINUITY_BREAKDOWN: "CONTINUITY_BREAKDOWN", // visit rhythm collapsed
  SPIRITUAL_FATIGUE:    "SPIRITUAL_FATIGUE",     // spiritual state is the dominant concern
  PRAYER_BURDEN:        "PRAYER_BURDEN",         // prayer load dominant, visits recent
  FOLLOWUP_REQUIRED:    "FOLLOWUP_REQUIRED",     // moderate gap — gentle nudge
  PASTORAL_OVERLOAD:    "PASTORAL_OVERLOAD",     // district: capacity exceeded

  // Positive / sustain
  RECOVERY_MOMENTUM:    "RECOVERY_MOMENTUM",    // recovering — sustain the rhythm
  GROWTH_WINDOW:        "GROWTH_WINDOW",         // baptism + positive trajectory

  // Quiet state
  HEALTHY_STABILITY:    "HEALTHY_STABILITY",    // all good — reduce UI
};

/* ============================= */
/* THEME → TONE                  */
/* One tone per theme — no       */
/* dynamic class interpolation   */
/* ============================= */

const THEME_TONE = {
  [NARRATIVE_THEME.CRISIS_CLUSTER]:       "red",
  [NARRATIVE_THEME.CONTINUITY_BREAKDOWN]: "orange",
  [NARRATIVE_THEME.SPIRITUAL_FATIGUE]:    "violet",
  [NARRATIVE_THEME.PRAYER_BURDEN]:        "red",
  [NARRATIVE_THEME.FOLLOWUP_REQUIRED]:    "amber",
  [NARRATIVE_THEME.PASTORAL_OVERLOAD]:    "orange",
  [NARRATIVE_THEME.RECOVERY_MOMENTUM]:    "blue",
  [NARRATIVE_THEME.GROWTH_WINDOW]:        "emerald",
  [NARRATIVE_THEME.HEALTHY_STABILITY]:    "emerald",
};

/* ============================= */
/* THEME CONTENT                 */
/* Static pastoral voice —       */
/* no template interpolation     */
/* ============================= */

const THEME_CONTENT = {
  [NARRATIVE_THEME.CRISIS_CLUSTER]: {
    summary:       "Varios factores críticos convergen esta semana",
    primaryAction: "Priorizar la visita y la oración antes de cualquier otra tarea",
  },
  [NARRATIVE_THEME.CONTINUITY_BREAKDOWN]: {
    summary:       "La continuidad pastoral se ha interrumpido",
    primaryAction: "Retomar el seguimiento con una visita esta semana",
  },
  [NARRATIVE_THEME.SPIRITUAL_FATIGUE]: {
    summary:       "El estado espiritual requiere acompañamiento pastoral",
    primaryAction: "Profundizar el acompañamiento espiritual en la próxima visita",
  },
  [NARRATIVE_THEME.PRAYER_BURDEN]: {
    summary:       "Hay peticiones de oración que requieren atención pastoral",
    primaryAction: "Interceder y acompañar en las peticiones activas",
  },
  [NARRATIVE_THEME.FOLLOWUP_REQUIRED]: {
    summary:       "El seguimiento pastoral necesita retomarse pronto",
    primaryAction: "Visitar en los próximos días para sostener el acompañamiento",
  },
  [NARRATIVE_THEME.PASTORAL_OVERLOAD]: {
    summary:       "La carga pastoral supera la capacidad sostenible esta semana",
    primaryAction: "Enfocarse en lo más urgente y delegar o postergar el resto",
  },
  [NARRATIVE_THEME.RECOVERY_MOMENTUM]: {
    summary:       "La continuidad pastoral está retomándose — momento clave",
    primaryAction: "Sostener el ritmo con otra visita cercana",
  },
  [NARRATIVE_THEME.GROWTH_WINDOW]: {
    summary:       "Hay un proceso pastoral positivo en marcha",
    primaryAction: "Acompañar con constancia el proceso en curso",
  },
  [NARRATIVE_THEME.HEALTHY_STABILITY]: {
    summary:       "El seguimiento pastoral está en buen ritmo",
    primaryAction: "Mantener el ritmo actual",
  },
};

/* ============================= */
/* SUPPRESSION MAP               */
/* Priority keys suppressed per  */
/* theme. These keys match those  */
/* from buildOperationalPriorities */
/* — if the narrative covers the  */
/* signal, the priority is hidden. */
/* ============================= */

const THEME_SUPPRESSED = {
  [NARRATIVE_THEME.CRISIS_CLUSTER]:       new Set(["visit", "prayer"]),
  [NARRATIVE_THEME.CONTINUITY_BREAKDOWN]: new Set(["visit"]),
  [NARRATIVE_THEME.SPIRITUAL_FATIGUE]:    new Set(["spiritual"]),
  [NARRATIVE_THEME.PRAYER_BURDEN]:        new Set(["prayer"]),
  [NARRATIVE_THEME.FOLLOWUP_REQUIRED]:    new Set(["visit"]),
  [NARRATIVE_THEME.PASTORAL_OVERLOAD]:    new Set([]),
  [NARRATIVE_THEME.RECOVERY_MOMENTUM]:    new Set(["visit"]),
  [NARRATIVE_THEME.GROWTH_WINDOW]:        new Set([]),
  [NARRATIVE_THEME.HEALTHY_STABILITY]:    new Set(["visit", "prayer", "spiritual"]),
};

/* ============================= */
/* VISIBLE WORKFLOW STAGES       */
/* ============================= */

const VISIBLE_STAGES = new Set([
  WORKFLOW_STAGES.INITIATED,
  WORKFLOW_STAGES.ACTIVE,
  WORKFLOW_STAGES.RECOVERING,
  WORKFLOW_STAGES.STALLED,
]);

/* ============================= */
/* NARRATIVE FALLBACK            */
/* Returned when there is        */
/* insufficient context to derive */
/* a narrative — UI stays quiet. */
/* ============================= */

const NARRATIVE_FALLBACK = {
  dominantTheme:     null,
  tone:              "slate",
  summary:           null,
  primaryAction:     null,
  supportingSignals: [],
  suppressedSignals: new Set(),
  confidence:        0,
  calmState:         true,
};

/* ============================= */
/* BUILD PASTORAL NARRATIVE      */
/* Entity orchestrator           */
/* ============================= */

/**
 * Synthesizes all pastoral signals for a person or family into one
 * dominant narrative object.
 *
 * The narrative coordinates all other engines — it does NOT render
 * anything itself. Its output drives:
 * - NarrativeStrip: what to show (summary + primaryAction)
 * - PriorityStack: what to suppress (suppressedSignals)
 * - UI state: whether to go fully quiet (calmState)
 *
 * All inputs are already computed — no new calculations happen here.
 *
 * @param {{
 *   workflows?:     Workflow[],
 *   memory?:        PastoralMemory | null,
 *   days?:          number | null,
 *   urgentPrayers?: number,
 *   activePrayers?: number,
 *   spiritualScore?: number | null,
 *   baptismStatus?: string | null,
 * }}
 * @returns {PastoralNarrative}
 */
export function buildPastoralNarrative({
  workflows      = [],
  memory         = null,
  days           = null,
  urgentPrayers  = 0,
  activePrayers  = 0,
  spiritualScore = null,
  baptismStatus  = null,
} = {}) {
  const trend = memory?.momentum?.trend ?? TREND_TYPES.UNKNOWN;

  const activeWorkflows = (workflows || []).filter((w) => VISIBLE_STAGES.has(w.stage));
  const hasCritical     = activeWorkflows.some((w) => w.urgency === WORKFLOW_URGENCY.CRITICAL);

  const hasSpiritual = spiritualScore !== null && spiritualScore <= 2;

  // ── CRISIS_CLUSTER ────────────────────────────────────────────────────────
  // Multiple dimensions converge: urgent prayers + absence + spiritual/critical
  const isCrisis =
    urgentPrayers > 0 &&
    (days === null || days > 45) &&
    (hasSpiritual || hasCritical);

  if (isCrisis) {
    return makeNarrative(NARRATIVE_THEME.CRISIS_CLUSTER, 0.95, [
      buildPrayerSignal(urgentPrayers, activePrayers),
      hasSpiritual ? "El estado espiritual también requiere atención" : null,
    ]);
  }

  // ── RECOVERY_MOMENTUM ─────────────────────────────────────────────────────
  // Recovering is positive — celebrate and sustain before anything else
  if (trend === TREND_TYPES.RECOVERING) {
    return makeNarrative(NARRATIVE_THEME.RECOVERY_MOMENTUM, 0.85, [
      urgentPrayers > 0 ? "Hay oraciones activas a acompañar en este proceso" : null,
    ]);
  }

  // ── CONTINUITY_BREAKDOWN ──────────────────────────────────────────────────
  // Visit rhythm collapsed — dominant signal
  const isCollapsed =
    (days === null && !hasCritical) ||
    (days !== null && days > 90)    ||
    trend === TREND_TYPES.STABLE_NEGLECTED;

  if (isCollapsed) {
    return makeNarrative(NARRATIVE_THEME.CONTINUITY_BREAKDOWN, 0.9, [
      urgentPrayers > 0 ? "Hay oraciones activas pendientes de atención"    : null,
      hasSpiritual      ? "El estado espiritual también requiere apoyo"      : null,
    ]);
  }

  // ── GROWTH_WINDOW ─────────────────────────────────────────────────────────
  // Baptism process + positive trajectory — evaluate before spiritual concerns
  // so a candidate being accompanied well is recognized as positive
  const isGrowth =
    baptismStatus === "estudiando" &&
    (trend === TREND_TYPES.STABLE_HEALTHY ||
     trend === TREND_TYPES.IMPROVING      ||
     trend === TREND_TYPES.RECOVERING);

  if (isGrowth) {
    return makeNarrative(NARRATIVE_THEME.GROWTH_WINDOW, 0.8, [
      "Proceso bautismal activo — continuar la preparación con constancia",
    ]);
  }

  // ── SPIRITUAL_FATIGUE ─────────────────────────────────────────────────────
  // Spiritual concern dominates when visit rhythm is still acceptable
  if (hasSpiritual && (days === null || days <= 90)) {
    return makeNarrative(NARRATIVE_THEME.SPIRITUAL_FATIGUE, 0.85, [
      days !== null ? `El seguimiento de visitas lleva ${days} días` : null,
    ]);
  }

  // ── PRAYER_BURDEN ─────────────────────────────────────────────────────────
  // Urgent prayers when visited recently — not a crisis, but needs care
  if (urgentPrayers > 0 && days !== null && days <= 45) {
    return makeNarrative(NARRATIVE_THEME.PRAYER_BURDEN, 0.8, [
      activePrayers > 1 ? `${activePrayers} peticiones activas en total` : null,
    ]);
  }

  // ── FOLLOWUP_REQUIRED ─────────────────────────────────────────────────────
  // Moderate gap — not yet critical, but the narrative gives a gentle nudge
  if (days !== null && days > 45 && days <= 90) {
    return makeNarrative(NARRATIVE_THEME.FOLLOWUP_REQUIRED, 0.75, [
      trend === TREND_TYPES.DECLINING
        ? "La frecuencia de visitas está disminuyendo respecto al historial"
        : null,
    ]);
  }

  // ── HEALTHY_STABILITY ─────────────────────────────────────────────────────
  if (
    trend === TREND_TYPES.STABLE_HEALTHY ||
    (days !== null && days <= 21 && urgentPrayers === 0 && !hasSpiritual)
  ) {
    return makeNarrative(NARRATIVE_THEME.HEALTHY_STABILITY, 0.9, []);
  }

  // ── FALLBACK — insufficient context ───────────────────────────────────────
  return NARRATIVE_FALLBACK;
}

/* ============================= */
/* BUILD DISTRICT NARRATIVE      */
/* District orchestrator         */
/*                               */
/* Returns a shape compatible    */
/* with WeeklyFocusCard `focus`  */
/* prop, extended with           */
/* dominantTheme + confidence +  */
/* suppressedSignals for future  */
/* district filtering.           */
/* ============================= */

/**
 * Synthesizes district-level workflows + load + prayers into a unified
 * narrative object. Used by useDistrictOperational → WeeklyFocusCard.
 *
 * The returned object is a superset of what WeeklyFocusCard expects:
 * { headline, context, tone, suggestions } — all present and compatible.
 * Additional fields (dominantTheme, confidence, suppressedSignals, calmState)
 * are ignored by WeeklyFocusCard and available for future district assistant use.
 *
 * @param {Workflow[]}  districtWorkflows — from buildDistrictWorkflows()
 * @param {PastoralLoad} load             — from estimatePastoralLoad()
 * @param {Object[]}    prayers           — active district prayers
 * @returns {DistrictNarrative}
 */
export function buildDistrictNarrative(districtWorkflows, load, prayers = []) {
  const active = (districtWorkflows || []).filter(
    (w) => w.stage !== WORKFLOW_STAGES.DORMANT && w.stage !== WORKFLOW_STAGES.SUSTAINED
  );

  const urgentPrayerCount = (prayers || []).filter(
    (p) => p.priority === "alta" && p.status === "activa"
  ).length;

  const criticalCount   = active.filter((w) => w.urgency === WORKFLOW_URGENCY.CRITICAL).length;
  const stalledCount    = active.filter((w) => w.stage === WORKFLOW_STAGES.STALLED).length;
  const recoveringCount = active.filter((w) => w.stage === WORKFLOW_STAGES.RECOVERING).length;
  const lvl             = load?.level ?? LOAD_LEVEL.LIGHT;

  // ── HEALTHY_STABILITY ─────────────────────────────────────────────────────
  if (active.length === 0 && urgentPrayerCount === 0) {
    return {
      dominantTheme:    NARRATIVE_THEME.HEALTHY_STABILITY,
      confidence:       0.95,
      headline:         "El distrito está en buen ritmo pastoral esta semana",
      context:          "No hay situaciones críticas activas — buen momento para visitas preventivas",
      tone:             FOCUS_TONE.HEALTHY,
      suggestions:      [],
      suppressedSignals: new Set(),
      calmState:        true,
    };
  }

  // ── PASTORAL_OVERLOAD ─────────────────────────────────────────────────────
  if (lvl === LOAD_LEVEL.OVERLOADED || (lvl === LOAD_LEVEL.HEAVY && criticalCount >= 3)) {
    return {
      dominantTheme:    NARRATIVE_THEME.PASTORAL_OVERLOAD,
      confidence:       0.9,
      headline:         "La carga pastoral esta semana está al límite del distrito",
      context:          "Enfocarse en lo urgente — no es posible atender todo simultáneamente",
      tone:             FOCUS_TONE.URGENT,
      suggestions:      buildDistrictSuggestions(active, urgentPrayerCount),
      suppressedSignals: new Set(),
      calmState:        false,
    };
  }

  // ── CRISIS_CLUSTER ─────────────────────────────────────────────────────────
  if (criticalCount >= 2 || (criticalCount >= 1 && urgentPrayerCount >= 2)) {
    const context = criticalCount > 1
      ? `${criticalCount} procesos en estado crítico requieren respuesta esta semana`
      : "Una situación crítica y peticiones urgentes activas convergen esta semana";
    return {
      dominantTheme:    NARRATIVE_THEME.CRISIS_CLUSTER,
      confidence:       0.9,
      headline:         "Hay varias situaciones críticas activas en el distrito",
      context,
      tone:             FOCUS_TONE.URGENT,
      suggestions:      buildDistrictSuggestions(active, urgentPrayerCount),
      suppressedSignals: new Set(),
      calmState:        false,
    };
  }

  // ── CONTINUITY_BREAKDOWN ───────────────────────────────────────────────────
  if (stalledCount >= 2) {
    return {
      dominantTheme:    NARRATIVE_THEME.CONTINUITY_BREAKDOWN,
      confidence:       0.85,
      headline:         "El seguimiento de varias iglesias necesita retomarse esta semana",
      context:          `${stalledCount} procesos pastorales han perdido continuidad`,
      tone:             FOCUS_TONE.NURTURING,
      suggestions:      buildDistrictSuggestions(active, urgentPrayerCount),
      suppressedSignals: new Set(),
      calmState:        false,
    };
  }

  // ── RECOVERY_MOMENTUM ─────────────────────────────────────────────────────
  if (recoveringCount >= 2 && stalledCount === 0 && criticalCount === 0) {
    return {
      dominantTheme:    NARRATIVE_THEME.RECOVERY_MOMENTUM,
      confidence:       0.8,
      headline:         "Varios procesos de recuperación pastoral están en marcha",
      context:          `${recoveringCount} procesos están retomando el ritmo — momento clave`,
      tone:             FOCUS_TONE.NURTURING,
      suggestions:      buildDistrictSuggestions(active, urgentPrayerCount),
      suppressedSignals: new Set(),
      calmState:        false,
    };
  }

  // ── FOLLOWUP_REQUIRED (district default) ──────────────────────────────────
  const processWord = active.length !== 1;
  const prayerWord  = urgentPrayerCount !== 1;
  const context = urgentPrayerCount > 0
    ? `${active.length} proceso${processWord ? "s" : ""} activo${processWord ? "s" : ""} · ${urgentPrayerCount} oración${prayerWord ? "es" : ""} urgente${prayerWord ? "s" : ""}`
    : `${active.length} proceso${processWord ? "s" : ""} pastoral${processWord ? "es" : ""} activo${processWord ? "s" : ""} en seguimiento`;

  return {
    dominantTheme:    NARRATIVE_THEME.FOLLOWUP_REQUIRED,
    confidence:       0.75,
    headline:         "Esta semana conviene mantener el seguimiento pastoral activo",
    context,
    tone:             FOCUS_TONE.BALANCED,
    suggestions:      buildDistrictSuggestions(active, urgentPrayerCount),
    suppressedSignals: new Set(),
    calmState:        false,
  };
}

/* ============================= */
/* INTERNAL HELPERS              */
/* ============================= */

function makeNarrative(theme, confidence, rawSupportingSignals) {
  const content    = THEME_CONTENT[theme];
  const tone       = THEME_TONE[theme]    ?? "amber";
  const suppressed = THEME_SUPPRESSED[theme] ?? new Set();

  // Filter nulls and cap at 2 supporting signals
  const supportingSignals = (rawSupportingSignals || [])
    .filter(Boolean)
    .slice(0, 2);

  return {
    dominantTheme:     theme,
    tone,
    summary:           content.summary,
    primaryAction:     content.primaryAction,
    supportingSignals,
    suppressedSignals: suppressed,
    confidence,
    calmState:         theme === NARRATIVE_THEME.HEALTHY_STABILITY,
  };
}

function buildPrayerSignal(urgentPrayers, activePrayers) {
  if (urgentPrayers > 1)  return `${urgentPrayers} oraciones urgentes activas`;
  if (urgentPrayers === 1) return "1 oración urgente activa";
  if (activePrayers > 0)  return `${activePrayers} oraciones activas en seguimiento`;
  return null;
}

const DISTRICT_SUGGESTION_TEXT = {
  [WORKFLOW_TYPES.CRISIS_RESPONSE]:    "Visitar familias con oración urgente activa",
  [WORKFLOW_TYPES.CHURCH_RECOVERY]:    "Atender las iglesias con mayor tiempo sin visita",
  [WORKFLOW_TYPES.VISIT_FOLLOWUP]:     "Retomar el seguimiento de iglesias estancadas",
  [WORKFLOW_TYPES.RECOVERY_PATH]:      "Sostener el ritmo de recuperación iniciado",
  [WORKFLOW_TYPES.SPIRITUAL_SUPPORT]:  "Profundizar el acompañamiento espiritual",
  [WORKFLOW_TYPES.PRAYER_ESCALATION]:  "Interceder con las peticiones urgentes activas",
  [WORKFLOW_TYPES.BAPTISM_PREPARATION]: "Dar continuidad al proceso bautismal",
};

function buildDistrictSuggestions(active, urgentPrayerCount) {
  const suggestions = [];
  const seen        = new Set();

  for (const w of active) {
    const text = DISTRICT_SUGGESTION_TEXT[w.type];
    if (text && !seen.has(w.type)) {
      seen.add(w.type);
      suggestions.push(text);
    }
    if (suggestions.length >= 3) break;
  }

  if (suggestions.length < 3 && urgentPrayerCount > 0 && !seen.has("prayers")) {
    suggestions.push("Interceder con las peticiones urgentes del distrito");
  }

  return suggestions.slice(0, 3);
}
